import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBlogPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const response = await fetch(`${API_URL}/blog/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog post');
      }
      const data = await response.json();
      setPost(data);
    } catch (err) {
      setError('Failed to load blog post. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">{error || 'Blog post not found'}</p>
            <Link
              to="/blog"
              className="mt-4 inline-block text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:hover:text-primary-600"
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Use SEO fields if available, fallback to defaults
  const seoTitle = post.metaTitle || post.title;
  const seoDescription = post.metaDescription || post.description;
  const seoKeywords = post.metaKeywords || 'Swift blog, SwiftUI articles, iOS development';
  const canonicalPath = post.slug ? `/blog/${post.slug}` : `/blog/${id}`;
  const fullUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}${canonicalPath}`
    : `https://swifttoolskit.com${canonicalPath}`;

  return (
    <>
      <SEO
        title={`${seoTitle} - SwiftToolsKit Blog`}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={canonicalPath}
        image={post.image || '/swifttoolskitlogo.png'}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: seoTitle,
            description: seoDescription,
            image: post.image ? (typeof window !== 'undefined' ? `${window.location.origin}${post.image}` : post.image) : undefined,
            datePublished: post.createdAt,
            dateModified: post.updatedAt,
            author: {
              '@type': 'Organization',
              name: 'SwiftToolsKit',
            },
            publisher: {
              '@type': 'Organization',
              name: 'SwiftToolsKit',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': fullUrl,
            },
            url: fullUrl,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://swifttoolskit.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: typeof window !== 'undefined' ? `${window.location.origin}/blog` : 'https://swifttoolskit.com/blog',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: seoTitle,
                item: fullUrl,
              },
            ],
          },
        ]}
        type="article"
      />
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:hover:text-primary-600 mb-8"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>

          <article>
            <h1 className="text-4xl font-bold mb-6">{post.title}</h1>
            
            {post.createdAt && (
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Published on {new Date(post.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}

            {post.image && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {post.description}
              </p>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
